/**
 * BandDistribution — PM dashboard headline KPI.
 *
 * Replaces "Average Risk Score" numeric KPIs with a per-band count row
 * + a mini stacked bar (proportional to distribution). Roger's ask
 * (May 2026): bring the Coordinator-view band scrub to the PM view —
 * raw averages across mixed-band portfolios are squishy and obscure
 * which clients actually need attention.
 *
 * Hard numbers (Revenue at Risk, Days to Recover, FTE counts, ARR,
 * client count) are NOT rendered through this component — they stay
 * as numerals. Only "squishy" aggregate scores get banded.
 */
import React from 'react';
import { portfolioDistribution } from '../../utils/bands';

const BAND_ORDER = ['critical', 'high', 'moderate', 'low'];
const BAND_META = {
  critical: { label: 'Critical', color: '#ef4444', icon: '●' },
  high:     { label: 'High',     color: '#f97316', icon: '■' },
  moderate: { label: 'Moderate', color: '#f59e0b', icon: '▲' },
  low:      { label: 'Low',      color: '#10b981', icon: '◆' },
};

/**
 * @param {object} props
 * @param {number[]} props.scores      Per-client risk scores (0-100); nulls skipped.
 * @param {string}   [props.title]     KPI label (e.g. "Portfolio Risk Posture").
 * @param {string}   [props.subtitle]  Optional sub-label.
 * @param {boolean}  [props.compact]   Slim variant for table headers / dense panels.
 */
export default function BandDistribution({ scores, title, subtitle, compact = false }) {
  const dist = portfolioDistribution(scores || []);

  if (dist.total === 0) {
    return (
      <div style={emptyShellStyle(compact)}>
        {title && <div style={titleStyle(compact)}>{title}</div>}
        <div style={{ fontSize: compact ? 12 : 14, color: '#9ca3af' }}>No client data</div>
      </div>
    );
  }

  return (
    <div style={shellStyle(compact)}>
      {title && (
        <div style={titleStyle(compact)}>
          {title}
          {subtitle && (
            <span style={{ fontSize: compact ? 11 : 12, color: '#6b7280', marginLeft: 8, fontWeight: 400 }}>
              {subtitle}
            </span>
          )}
        </div>
      )}

      {/* Stacked bar */}
      <div style={stackedBarShell(compact)} role="img" aria-label={ariaLabel(dist)}>
        {BAND_ORDER.map(band => {
          const count = dist[band];
          if (count === 0) return null;
          const pct = (count / dist.total) * 100;
          return (
            <div
              key={band}
              style={{
                width: `${pct}%`,
                background: BAND_META[band].color,
                height: '100%',
              }}
              title={`${count} ${BAND_META[band].label}`}
            />
          );
        })}
      </div>

      {/* Chip row — only render bands with count > 0 */}
      <div style={chipRowStyle(compact)}>
        {BAND_ORDER.map(band => {
          const count = dist[band];
          if (count === 0) return null;
          const meta = BAND_META[band];
          return (
            <span key={band} style={chipStyle(meta, compact)}>
              <span aria-hidden="true" style={{ marginRight: 4 }}>{meta.icon}</span>
              <strong style={{ fontWeight: 600 }}>{count}</strong>
              <span style={{ marginLeft: 4 }}>{meta.label}</span>
            </span>
          );
        })}
        <span style={{ marginLeft: 'auto', fontSize: compact ? 11 : 12, color: '#6b7280' }}>
          {dist.total} {dist.total === 1 ? 'client' : 'clients'}
        </span>
      </div>
    </div>
  );
}

function ariaLabel(dist) {
  const parts = BAND_ORDER
    .filter(b => dist[b] > 0)
    .map(b => `${dist[b]} ${BAND_META[b].label}`);
  return `Portfolio risk band distribution: ${parts.join(', ')}`;
}

// ── inline styles (kept self-contained; existing PM cards mix Tailwind + inline) ──

function shellStyle(compact) {
  return {
    display: 'flex',
    flexDirection: 'column',
    gap: compact ? 6 : 10,
    padding: compact ? '8px 10px' : '14px 16px',
    borderRadius: 8,
    background: 'rgba(255, 255, 255, 0.02)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
  };
}

function emptyShellStyle(compact) {
  return { ...shellStyle(compact), opacity: 0.6 };
}

function titleStyle(compact) {
  return {
    fontSize: compact ? 12 : 13,
    fontWeight: 600,
    color: '#d1d5db',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  };
}

function stackedBarShell(compact) {
  return {
    display: 'flex',
    width: '100%',
    height: compact ? 6 : 10,
    borderRadius: compact ? 3 : 5,
    overflow: 'hidden',
    background: 'rgba(255, 255, 255, 0.04)',
  };
}

function chipRowStyle(compact) {
  return {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: compact ? 6 : 10,
    fontSize: compact ? 12 : 13,
  };
}

function chipStyle(meta, compact) {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    padding: compact ? '2px 8px' : '4px 10px',
    borderRadius: 999,
    background: `${meta.color}1A`, // ~10% alpha
    color: meta.color,
    border: `1px solid ${meta.color}4D`, // ~30% alpha
    fontWeight: 500,
  };
}
